// HomeFaq — single consolidated FAQ rendered at the bottom of the home page.
//
// Previously the site had several FAQs scattered around: one per tool (via
// ToolFaqSection + seo/toolFaqs.js), plus dedicated FAQs on Pricing,
// Influencer and Vs. They were all removed in favour of this single
// consolidated FAQ so the home page becomes the canonical answer hub.
//
// Why a flat, hand-curated list:
//   • Catalogue questions cover the product, pricing, materials,
//     account/downloads and partnerships — the union of what the old
//     per-page FAQs answered, distilled into ~8 questions per locale.
//   • Each entry is localised PT/EN/ES inline (no i18n key explosion)
//     and rendered as a native <details>/<summary> accordion for free
//     a11y + zero JS state.
//   • A matching FAQPage JSON-LD payload is produced by `buildHomeFaqJsonLd`
//     and emitted by Home.jsx so Google can attach the rich-result.
//
// Usage:
//   import HomeFaq, { buildHomeFaqJsonLd } from '../components/HomeFaq';
//   ...
//   <Seo jsonLd={[..., buildHomeFaqJsonLd(lang)]} />
//   ...
//   <HomeFaq />     // mount near the bottom of the home <main>

import { ChevronDown } from 'lucide-react';
import { useI18n } from '../i18n';

// ─── Q&A content ─────────────────────────────────────────────────────
// One canonical list per locale. Keep entries short and intent-focused;
// long-form copy belongs in the blog. Order is roughly:
//   product → pricing → materials → account/files → partnerships.
const FAQ_CONTENT = {
  pt: {
    heading: 'Perguntas frequentes',
    subheading: 'Tudo o que você precisa saber antes de cortar a primeira peça.',
    items: [
      {
        q: 'O que é o MakerCutt?',
        a: 'O MakerCutt é uma plataforma web que gera arquivos DXF e SVG prontos para corte a laser e CNC. Você ajusta as dimensões, espessura, tipo de encaixe e padrões em formulários simples — e baixa o arquivo na hora, sem precisar de software de CAD.',
      },
      {
        q: 'Preciso pagar para usar?',
        a: 'Não. As ferramentas principais (caixas básicas, luminárias, displays, halftone e muitas outras) são gratuitas. O plano Pro libera ferramentas avançadas, mais cota de IA e remove o limite de downloads — é opcional e você pode cancelar a qualquer momento.',
      },
      {
        q: 'Quanto custa o Pro?',
        a: 'Cerca de US$ 7/mês (ou o equivalente em BRL). Há 7 dias de trial grátis com tudo do Pro liberado e garantia de reembolso de 7 dias após o primeiro pagamento. Veja preços atualizados e detalhes na página /pricing.',
      },
      {
        q: 'Quais materiais posso cortar?',
        a: 'Os arquivos funcionam para MDF, compensado, acrílico, cartonado, EVA, couro e qualquer outro material laser-compatível. Você informa a espessura e o kerf da sua máquina — o gerador ajusta as folgas dos encaixes automaticamente.',
      },
      {
        q: 'Posso usar comercialmente os arquivos gerados?',
        a: 'Sim, em qualquer plano. Os DXF/SVG gerados são seus, livres de royalties — venda, cobre, distribua sem restrições. O MakerCutt não reivindica direitos sobre os arquivos baixados.',
      },
      {
        q: 'Como faço o download e como recupero arquivos comprados?',
        a: 'Após pagar (ou se já tiver Pro), o download começa automático. Compras avulsas ficam vinculadas ao seu e-mail: se você fechar a aba sem baixar, o site mostra um banner de recuperação na próxima visita por até 7 dias.',
      },
      {
        q: 'Em quais idiomas funciona?',
        a: 'A interface está em português, inglês e espanhol. Os arquivos gerados não têm idioma — são puro vetor — então funcionam em qualquer software (LightBurn, RDWorks, LaserGRBL, Inkscape, Illustrator, Fusion 360 etc).',
      },
      {
        q: 'Tenho um programa de embaixadores ou afiliados?',
        a: 'Sim. Influencers e criadores podem se candidatar em /influencer para ganhar acesso Pro em troca de conteúdo combinado. Não usamos APIs nem scraping — um humano avalia seu perfil ao vivo no Instagram.',
      },
    ],
  },
  en: {
    heading: 'Frequently asked questions',
    subheading: 'Everything you need to know before cutting your first piece.',
    items: [
      {
        q: 'What is MakerCutt?',
        a: 'MakerCutt is a web app that generates DXF and SVG files ready for laser and CNC cutting. You tweak dimensions, thickness, joint type and patterns in simple forms — and download the file instantly, no CAD software required.',
      },
      {
        q: 'Do I have to pay to use it?',
        a: 'No. The main tools (basic boxes, lamps, displays, halftone and many more) are free. The Pro plan unlocks advanced tools, more AI quota and removes the per-download limit — it is optional and you can cancel any time.',
      },
      {
        q: 'How much does Pro cost?',
        a: 'About US$ 7/month (or local equivalent). There is a 7-day free trial with every Pro feature unlocked and a 7-day money-back guarantee after the first payment. See live pricing and details at /pricing.',
      },
      {
        q: 'What materials can I cut?',
        a: 'Files work with MDF, plywood, acrylic, cardboard, EVA, leather and any other laser-friendly material. You enter your thickness and kerf — the generator adjusts joint tolerances for you automatically.',
      },
      {
        q: 'Can I use the generated files commercially?',
        a: 'Yes, on any plan. The DXF/SVG you download are yours, royalty-free — sell, charge, distribute without restrictions. MakerCutt does not claim rights over downloaded files.',
      },
      {
        q: 'How do downloads work and how do I recover purchased files?',
        a: 'Once payment goes through (or if you are on Pro), the download starts automatically. One-off purchases are tied to your email: if you close the tab before saving the file, the site shows a recovery banner for up to 7 days on your next visit.',
      },
      {
        q: 'Which languages does it support?',
        a: 'The interface is available in English, Portuguese and Spanish. The generated files have no language — pure vector geometry — so they work in any software (LightBurn, RDWorks, LaserGRBL, Inkscape, Illustrator, Fusion 360, etc).',
      },
      {
        q: 'Is there an ambassador or affiliate programme?',
        a: 'Yes. Creators and influencers can apply at /influencer to earn Pro access in exchange for agreed content. We do not use APIs or scraping — a human reviews your Instagram profile live.',
      },
    ],
  },
  es: {
    heading: 'Preguntas frecuentes',
    subheading: 'Todo lo que necesitas saber antes de cortar tu primera pieza.',
    items: [
      {
        q: '¿Qué es MakerCutt?',
        a: 'MakerCutt es una web app que genera archivos DXF y SVG listos para corte láser y CNC. Ajustas dimensiones, espesor, tipo de encaje y patrones en formularios simples — y descargas el archivo al instante, sin software CAD.',
      },
      {
        q: '¿Tengo que pagar para usarlo?',
        a: 'No. Las herramientas principales (cajas básicas, lámparas, displays, halftone y muchas más) son gratis. El plan Pro libera herramientas avanzadas, más cuota de IA y elimina el límite de descargas — es opcional y puedes cancelar cuando quieras.',
      },
      {
        q: '¿Cuánto cuesta el Pro?',
        a: 'Cerca de US$ 7/mes (o equivalente local). Hay 7 días de trial gratis con todo Pro liberado y garantía de reembolso de 7 días tras el primer pago. Precios y detalles en vivo en /pricing.',
      },
      {
        q: '¿Qué materiales puedo cortar?',
        a: 'Los archivos funcionan con MDF, contrachapado, acrílico, cartón, EVA, cuero y cualquier otro material láser-compatible. Indicas espesor y kerf — el generador ajusta las tolerancias automáticamente.',
      },
      {
        q: '¿Puedo usar los archivos comercialmente?',
        a: 'Sí, en cualquier plan. Los DXF/SVG que descargas son tuyos, sin regalías — vende, cobra, distribuye sin restricciones. MakerCutt no reclama derechos sobre los archivos descargados.',
      },
      {
        q: '¿Cómo funcionan las descargas y cómo recupero archivos comprados?',
        a: 'Una vez confirmado el pago (o si tienes Pro), la descarga arranca automáticamente. Las compras únicas quedan ligadas a tu email: si cierras la pestaña sin guardar el archivo, el sitio muestra un banner de recuperación hasta 7 días en tu próxima visita.',
      },
      {
        q: '¿En qué idiomas funciona?',
        a: 'La interfaz está disponible en español, portugués e inglés. Los archivos generados no tienen idioma — son pura geometría vectorial — así que funcionan en cualquier software (LightBurn, RDWorks, LaserGRBL, Inkscape, Illustrator, Fusion 360, etc).',
      },
      {
        q: '¿Hay programa de embajadores o afiliados?',
        a: 'Sí. Creadores e influencers pueden postularse en /influencer para ganar acceso Pro a cambio de contenido acordado. No usamos APIs ni scraping — una persona revisa tu perfil de Instagram en vivo.',
      },
    ],
  },
};

// Resolve the FAQ content for a given language, falling back to PT then
// EN so a missing locale never blanks the section.
function resolveFaq(lang) {
  return FAQ_CONTENT[lang] || FAQ_CONTENT.en || FAQ_CONTENT.pt;
}

// ─── JSON-LD builder ─────────────────────────────────────────────────
// Mirrors the visible accordion so Google's FAQPage rich result has
// matching DOM content (required since 2023). Emit ONLY for the home
// page because Google de-duplicates FAQPage entities by URL anyway.
export function buildHomeFaqJsonLd(lang = 'pt') {
  const { items } = resolveFaq(lang);
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: it.a,
      },
    })),
  };
}

// ─── Component ───────────────────────────────────────────────────────

export default function HomeFaq() {
  const { lang } = useI18n();
  const { heading, subheading, items } = resolveFaq(lang);

  return (
    <section
      id="faq"
      aria-labelledby="home-faq-heading"
      className="bg-zinc-50 border-t border-zinc-200"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2
          id="home-faq-heading"
          className="font-chunky text-2xl sm:text-3xl text-zinc-950 mb-1"
        >
          {heading}
        </h2>
        <p className="text-sm text-zinc-600 mb-6 sm:mb-8">{subheading}</p>

        <ul className="space-y-2">
          {items.map((it, idx) => (
            <li key={idx}>
              <details className="group rounded-lg border border-zinc-200 bg-white open:bg-white open:shadow-sm transition-colors">
                <summary
                  className="cursor-pointer list-none flex items-start justify-between gap-3 px-4 py-3 sm:py-3.5 select-none"
                  data-testid={`home-faq-q-${idx}`}
                >
                  <span className="font-chunky text-[13px] sm:text-[14px] text-zinc-900 leading-snug pr-2">
                    {it.q}
                  </span>
                  <ChevronDown
                    className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <div
                  className="px-4 pb-4 sm:pb-5 pt-1 text-[13px] sm:text-[14px] text-zinc-700 leading-relaxed"
                  data-testid={`home-faq-a-${idx}`}
                >
                  {it.a}
                </div>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
